import React, { useEffect, useState } from 'react';

import {
  HiOutlineChevronRight,
  HiOutlineChevronLeft,
  HiChevronDown,
  HiChevronUp,
  HiOutlineLogout,
  HiArrowSmLeft,
  HiMenu,
} from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as HomeIcon } from 'assets/icons/home.svg';
import { ReactComponent as Logout } from 'assets/icons/logout.svg';
import Footer from 'components/shared/Layouts/Footer';
import Drawer from 'components/shared/Modal/Drawer';
import { BranchCompany, BranchUser } from 'components/shared/ui/UserInfo';
import { device } from 'config/device';
import { publicWindow } from 'config/public';
import { useUserConfiguration } from 'config/themes';
import { useAuth, useAuthDispatch } from 'context/AuthProvider';

import AccountMenu from './AccountMenu';
import ExpandableAccount from './ExpandableAccount';
import {
  CloseIcon,
  NavlinksWrapper,
  NavMenuWrapper,
  NavStaticLinkWrapper,
  NavMenuToggler,
  NavMobileMenuToggler,
  ExpandableContainer,
  ExpandButton,
} from './NavMenu.style';
import NavMenuLink from './NavMenuLink';
import { NavLogo, AccountIconLine } from '../NavBar.style';

const { ssoSn } = publicWindow;
const { ssoRedirectUrl } = publicWindow;

type NavMenuProps = {
  isOverFlown: boolean;
  setMenuExpanded: (expanded: boolean) => void;
};

// Helper function to group objects by GROUPNAME
function renderGroupedMenu(objects: any[], closeMenu: () => void) {
  // Group objects by GROUPNAME and track order of first appearance
  const grouped: { [key: string]: any[] } = {};
  const groupOrder: string[] = [];
  const ungrouped: any[] = [];

  objects.forEach((object: any) => {
    const group = object.GROUPNAME || '';
    if (group === '') {
      ungrouped.push(object);
    } else {
      if (!grouped[group]) {
        grouped[group] = [];
        groupOrder.push(group); // Track order of first appearance
      }
      grouped[group].push(object);
    }
  });

  const result: any[] = [];

  // First, render grouped items (ordered by first appearance)
  groupOrder.forEach((groupName) => {
    result.push(
      <MenuGroup
        key={groupName}
        groupName={groupName}
        objects={grouped[groupName]}
        closeMenu={closeMenu}
      />,
    );
  });

  // Then render ungrouped items (in original order)
  ungrouped.forEach((object: any, index: number) => {
    // Add margin to first ungrouped item if there are groups
    const shouldAddMargin = groupOrder.length > 0 && index === 0;
    result.push(
      <div
        key={`spacer-${object.OBJECTPATH}`}
        style={{ marginTop: shouldAddMargin ? '1.5rem' : '0' }}
      >
        <NavMenuLink object={object} closeMenu={closeMenu} />
      </div>,
    );
  });

  return result;
}

// Collapsible Menu Group Component
function MenuGroup({
  groupName,
  objects,
  closeMenu,
}: {
  groupName: string;
  objects: any[];
  closeMenu: () => void;
}) {
  const [isExpanded, setIsExpanded] = React.useState(true);

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          padding: '1rem 1.5rem',
          fontWeight: 'bold',
          fontSize: '1.2rem',
          color: '#333',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          border: '1px solid #e0e0e0',
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#e8f4fd';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#f5f5f5';
        }}
      >
        <span style={{ fontWeight: '700', fontSize: '1.4rem', letterSpacing: '0.5px' }}>
          {groupName}
        </span>
        <span
          style={{
            fontSize: '1.4rem',
            transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          ▼
        </span>
      </div>

      <div
        style={{
          overflow: 'hidden',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          maxHeight: isExpanded ? '1000px' : '0px',
          opacity: isExpanded ? 1 : 0,
        }}
      >
        <div
          style={{
            paddingLeft: '0.5rem',
            marginLeft: '1rem',
            marginTop: '0.5rem',
            transform: isExpanded ? 'translateY(0)' : 'translateY(-10px)',
            transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {objects.map((object: any, index: number) => (
            <div
              key={`group-item-${object.OBJECTPATH}`}
              style={{ marginTop: !isExpanded && index === 0 ? '1.5rem' : '0' }}
            >
              <NavMenuLink object={object} closeMenu={closeMenu} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function NavMenu({ isOverFlown, setMenuExpanded }: NavMenuProps) {
  const [issuer, setIssuer] = useState('');
  const [isOpen, setIsOpen] = useState(() => {
    // Check if we're on mobile
    const isMobile = window.matchMedia(device.mobile).matches;
    if (isMobile) {
      return false;
    }
    // For desktop, use localStorage
    const savedState = localStorage.getItem('navMenuIsOpen');
    return savedState ? JSON.parse(savedState) : true;
  });

  const navigate = useNavigate();
  const { logoPath, logoHeight } = useUserConfiguration();

  const authState = useAuth();
  const authSend = useAuthDispatch();
  const isAuthorized = authState.matches('authorized');
  const isInitialized = authState.matches('authorized.initialized');
  const { objects = [], user, branch } = authState.context;

  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => setIsExpanded(!isExpanded);

  useEffect(() => {
    const storedValue = localStorage.getItem('issuer');
    if (storedValue) {
      setIssuer(storedValue);
    }
    setMenuExpanded(isOpen);

    // Only save to localStorage if we're on desktop
    const isMobile = window.matchMedia(device.mobile).matches;
    if (!isMobile) {
      localStorage.setItem('navMenuIsOpen', JSON.stringify(isOpen));
    }
  }, [isOpen]);

  function goHome() {
    setIsOpen(false);
    navigate('/');
  }

  function logout() {
    if (issuer) {
      localStorage.clear();
      window.location.href =
        'https://sso.softonecloud.com/SAML/InitiateSingleLogout/' +
        `${ssoSn}` +
        '?issuer=' +
        `${issuer}` +
        '&redirectUrl=' +
        `${ssoRedirectUrl}`;
      return;
    }
    localStorage.clear();
    setIsOpen(false);
    authSend({ type: 'LOGOUT' });
    navigate('/', { replace: true });
  }

  if (!isAuthorized) return null;
  return (
    <NavMenuWrapper $isOverFlown={isOverFlown}>
      <Drawer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        renderHeader={() => (
          <>
            <NavLogo
              alt='app-logo'
              src={logoPath}
              $logoHeight={logoHeight}
              onClick={() => navigate('/')}
            />
            {/* <CloseIcon onClick={() => setIsOpen(false)} /> */}
          </>
        )}
        renderInfo={() => <></>}
        renderFooter={() => (
          <>
            <ExpandButton onClick={toggleExpand}>
              <AccountIconLine />
              Account
              {isExpanded ? <HiChevronDown /> : <HiChevronUp />}
            </ExpandButton>

            <ExpandableContainer isOpen={isExpanded}>
              <BranchUser {...{ user, branch }} />
              <BranchCompany {...{ branch }} />
              <ExpandableAccount closeMenu={() => setIsOpen(false)} />
              <div className='logoutBtn' onClick={logout}>
                Logout
                <HiOutlineLogout />
              </div>
            </ExpandableContainer>

            <Footer screen='leftMenuStandar' />
          </>
        )}
      >
        <NavlinksWrapper style={{ margin: '0' }}>
          <NavStaticLinkWrapper className='activeMenuItem' onClick={goHome}>
            Home
          </NavStaticLinkWrapper>
          {isInitialized && renderGroupedMenu(objects, () => setIsOpen(false))}
        </NavlinksWrapper>
      </Drawer>

      <NavMenuToggler
        onClick={() => setIsOpen((prevIsOpen: boolean) => !prevIsOpen)}
        exit={{ marginLeft: 0 }}
        animate={isOpen ? { marginLeft: 'calc(30rem + 0px)' } : { marginLeft: 0 }}
        transition={{ duration: 0.3, ease: 'linear' }}
      >
        {isOpen ? <HiOutlineChevronLeft /> : <HiOutlineChevronRight />}
      </NavMenuToggler>

      <NavMobileMenuToggler>
        <div className='floatMobileMenu'>
          <HiArrowSmLeft
            onClick={() => {
              navigate(-1);
            }}
          />
          <HiMenu onClick={() => setIsOpen((prevIsOpen: boolean) => !prevIsOpen)} />
        </div>
      </NavMobileMenuToggler>
    </NavMenuWrapper>
  );
}

export default NavMenu;
